/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import { createMachine, assign } from 'xstate';
import { throttle } from 'lodash';
import { shouldDoEchoCancel } from '../hooks/use-echo-cancellation';

export const videoActionCommands = {
	LOADED: 'LOADED',
	FAIL: 'FAIL',
	TIMING: 'TIMING',
	TIMECHANGE: 'TIMECHANGE',
	MUTE: 'MUTE',
	UNMUTE: 'UNMUTE',
	VOLUME: 'VOLUME',
	PLAY: 'PLAY',
	PAUSE: 'PAUSE',
	SCRUB: 'SCRUB',
	STOPSCRUBBING: 'STOPSCRUBBING',
	END: 'END',
};

interface VideoMachineContext {
	video: HTMLVideoElement | null;
	audio: HTMLAudioElement | null;
	paused: boolean;
	duration: number;
	elapsed: number;
	volume: number;
	shownVolume: number;
	scrubbing: boolean;
	updatePresentedMedia: any;
	adjustedVolume: (volume: number) => number;
	muted: boolean;
	wasEnded: boolean;
	updateMediaControlsContext: any;
	id: number | null;
	doFireSync: boolean;
	isForShotEditorModal: boolean;
}

export const initialContext: VideoMachineContext = {
	video: null,
	audio: null,
	paused: true,
	duration: 0,
	elapsed: 0,
	volume: 1,
	shownVolume: 1,
	scrubbing: false,
	updatePresentedMedia: null,
	adjustedVolume: (volume:number) => volume,
	muted: false,
	wasEnded: false,
	updateMediaControlsContext: () => null,
	id: null,
	doFireSync: false,
	isForShotEditorModal: false,
};

const endScrubCondition = (context: any, event: any) => context.wasEnded;

const shouldEchoCancel = shouldDoEchoCancel();

export const videoMachine = createMachine({
	id: 'video',
	initial: 'loading',
	context: initialContext,
	predictableActionArguments: true,
	states: {
		loading: {
			on: {
				[videoActionCommands.LOADED]: {
					target: 'ready',
					actions: ['setVideo', 'setVolume'],
				},
				[videoActionCommands.FAIL]: 'failure',
			},
		},
		ready: {
			type: 'parallel',
			on: {
				[videoActionCommands.TIMECHANGE]: {
					actions: ['setCurrentTime'],
				},
				[videoActionCommands.LOADED]: {
					target: 'ready',
					actions: ['setVideo'],
				},
			},
			states: {
				volume: {
					initial: 'unmuted',
					states: {
						muted: {
							on: {
								[videoActionCommands.UNMUTE]: 'unmuted',
								[videoActionCommands.VOLUME]: {
									target: 'otherVolume',
									actions: ['setVolume'],
								},
							},
							entry: ['setVolumeMuted'],
						},
						unmuted: {
							on: {
								[videoActionCommands.MUTE]: 'muted',
								[videoActionCommands.VOLUME]: {
									target: 'otherVolume',
									actions: ['setVolume'],
								},
							},
							entry: ['setVolumeUnmuted'],
						},
						otherVolume: {
							always: 'unmuted',
						},
					},
				},
				navigating: {
					initial: 'playpause',
					states: {
						scrub_end: {
							states: {
								scrubbing: {
									id: 'scrubbing',
									on: {
										[videoActionCommands.STOPSCRUBBING]: [
											{
												target: '#paused',
												actions: ['chooseTime', 'setScrubbingFalse', 'setWasEndedFalse'],
												cond: endScrubCondition,
											},
											{
												target: '#playpause.hist',
												actions: ['chooseTime', 'setScrubbingFalse', 'setWasEndedFalse'],
											},
										],
										[videoActionCommands.TIMECHANGE]: {
											actions: ['setCurrentTime'],
										},
									},
									entry: ['setCurrentTime', 'pauseVideo', 'setPauseTrue', 'setScrubbingTrue'],
								},
								video_ended: {
									id: 'ended',
									on: {
										[videoActionCommands.PLAY]: {
											target: '#playpause.playing',
											actions: 'restartVideo',
										},
										[videoActionCommands.SCRUB]: {
											target: 'scrubbing',
											actions: 'setWasEndedTrue',
										},
									},
									entry: ['onEndTrigger'],
								},
							},
						},
						playpause: {
							id: 'playpause',
							initial: 'paused',
							states: {
								hist: {
									type: 'history',
								},
								paused: {
									id: 'paused',
									on: {
										[videoActionCommands.PLAY]: {
											target: 'playing',
											actions: ['setPauseFalse', 'setElapsed'],
										},
										[videoActionCommands.SCRUB]: {
											target: '#scrubbing',
											actions: ['setScrubbingTrue'],
										},
									},
									entry: ['pauseVideo'],
								},
								playing: {
									initial: 'timing',
									states: {
										timing: {
											on: {
												[videoActionCommands.TIMING]: {
													target: 'timing',
													actions: 'setElapsed',
												},
											},
										},
									},
									on: {
										[videoActionCommands.PAUSE]: {
											target: 'paused',
											actions: ['setPauseTrue', 'setElapsed', 'pauseVideo'],
										},
										[videoActionCommands.SCRUB]: {
											target: '#scrubbing',
											actions: ['setScrubbingTrue'],
										},
										[videoActionCommands.END]: {
											target: '#ended',
										},
									},
									entry: ['playVideo'],
								},
							},
						},
					},
				},
			},
		},
		failure: {
			entry: ['failureFunction'],
			type: 'final',
		},
	},
});

/**
 * Action functions
 */

const failureFunction = (context: any, _event: any) => {
	// eslint-disable-next-line no-console
	console.error('The video failed', _event);
};

const setVideo = assign({
	video: (_context, event: any) => event.video,
	audio: (_context, event: any) => event.audio,
	updatePresentedMedia: (_context, event: any) => event.updatePresentedMedia,
	duration: (_context, event: any) => (
		event?.video?.duration ?? event?.video?.getDuration?.() ?? 0
	),
	shownVolume: (_context, event: any) => event.shownVolume,
	muted: (_context, event: any) => event.muted,
	updateMediaControlsContext: (_context, event: any) => event.updateMediaControlsContext,
	id: (_context, event: any) => String(event.mediaId),
	isForShotEditorModal: (_context, event: any) => event.isForShotEditorModal,
});

const setElapsed = assign({
	elapsed: (context: any, _event: any) => {
		context.updateMediaControlsContext?.({
			elapsed: context.elapsed, id: context.id, state: { ...context }, from: 'setElapsed In Video Machine',
		});
		if (context.video?.getPosition) {
			// Control for possible NaN
			return context?.video?.getPosition() || 0;
		}

		return context?.video?.currentTime;
	},
});

const setWasEndedFalse = (context: any) => {
	context.wasEnded = false;
};
const setWasEndedTrue = (context: any) => {
	context.wasEnded = true;
};

const setCurrentTime = (context: any, _event: any) => {
	// console.log(`setCurrentTime+ ${_event.from}`);
	let currTime = parseFloat(_event.currentTime);

	// if a video has looped without any user interaction,
	// the current time will include the full duration of the video for each time it has played
	const withinAcceptable = Math.abs(_event.duration - currTime);
	if (currTime > _event.duration
		&& _event.duration !== 0
		&& !context.wasEnded
		&& _event.duration !== currTime
		&& withinAcceptable > 0.5) {
		currTime %= _event.duration;
	}

	// do not reassign these if they have not changed
	// this can trigger events to fire again when they should not
	if (context.video.currentTime !== currTime) {
		context.video.currentTime = currTime;
		context.video?.seekTo?.(currTime);
	}
	context.audio.currentTime = currTime;
	context.elapsed = currTime;
	context.updateMediaControlsContext?.({ id: context.id, state: { ...context }, from: 'setCurrentTime In Video Machine' });
};

const chooseTime = (context: any, _event: any) => {
	// console.log(`chooseTime+ ${_event.from}`);
	context.updatePresentedMedia({ timeElapsed: context.elapsed });
	context.updateMediaControlsContext?.({ id: context.id, state: { ...context }, from: 'setCurrentTime In Video Machine' });
};

const setPauseFalse = (context: any, _event: any) => {
	// console.log(`setPauseFalse+ ${_event.from}`);
	context.paused = false;
};

const setPauseTrue = (context: any, _event: any) => {
	// console.log(`setPauseTrue+ ${_event.from}`);
	context.paused = true;
};

// eslint-disable-next-line no-unused-vars
const setScrubbingFalse = (context: any, _event: any) => {
	// console.log(`setScrubbingFalse+ ${_event.from}`);
	context.scrubbing = false;
};
const setScrubbingTrue = (context: any, _event: any) => {
	// console.log(`setScrubbingTrue+ ${_event.from}`);
	context.scrubbing = true;
};

const playVideo = async (context: any, _event: any) => {
	// console.log(`playVideo+ ${_event.from}`);
	// due to the step property on the scrubbar being set to 0.02,
	// a user may try to scrub to the end of the video but still be less than the duration
	const scrubbedToEnd = context.video.duration - context.video.currentTime < 0.02;
	context.paused = scrubbedToEnd;

	try {
		const playResult = await context.video.play();
		if (!context.muted) {
			const videoMutedVolume = shouldEchoCancel
				? 0.00001
				: context.volume;

			context.video.volume = videoMutedVolume;
			context.video.setVolume?.(videoMutedVolume);
		}

		if (playResult) {
			context.audio.play();
		}
	} catch (e) {
		// console.log(e);
	}

	if (_event.doFireSync || _event.type === 'STOPSCRUBBING') {
		context.updatePresentedMedia?.({ paused: scrubbedToEnd, timeElapsed: context.elapsed });
	}
	context.updateMediaControlsContext?.({ id: context.id, state: { ...context, paused: false }, from: 'playVideo In Video Machine' });
};

const pauseVideo = (context: any, _event: any) => {
	// console.log(`pauseVideo+ ${_event.from}`);
	function pause() {
		context.video.pause();
		context.audio.pause();
		context.paused = true;
	}
	// console.log(`pauseVideo+ ${_event.from}`);
	if (_event.doFireSync && _event.type === 'LOADED') {
		// on load we were running into issues with synced state being
		// set to paused while trying to autoplay, so on LOADED
		// we now check if we want to autoplay and if we do we do not pause
		const isPaused = _event.paused ?? true;
		if (isPaused) {
			pause();
		}
		context.updatePresentedMedia({ paused: isPaused, timeElapsed: context.elapsed });
	} else if (_event.doFireSync || _event.type === 'STOPSCRUBBING' || _event.type === 'END') {
		pause();
		context.updatePresentedMedia({ paused: true, timeElapsed: context.elapsed });
	} else {
		pause();
	}
	context.updateMediaControlsContext?.({ id: context.id, state: { ...context }, from: 'pauseVideo In Video Machine' });
};

const onEndTrigger = (context: any, _event: any) => {
	// console.log(`onEndTrigger+ ${_event.from}`);
	context.video.pause();
	context.audio.pause();
	context.paused = true;
	context.wasEnded = true;
	// clients may experience a pause 1-2 seconds before their video has fully ended
	context.updatePresentedMedia({ paused: true, timeElapsed: context.elapsed }, false);
	context.updateMediaControlsContext?.({ id: context.id, state: { ...context }, from: 'onEndTrigger In Video Machine' });
};

const restartVideo = async (context: any, _event: any) => {
	// console.log(`restartVideo+ ${_event.from}`);
	if (!_event.currentTime) {
		context.video.currentTime = 0;
		context.elapsed = 0;
		context.video?.seekTo?.(0);
	}
	context.wasEnded = false;
};

const throttledUpdatePresentedMedia = throttle((context: any, _event: any) => {
	const { currentMediaPage } = _event;
	context.updatePresentedMedia(
		{
			volume: context.shownVolume,
			muted: _event.type === 'LOADED' && _event.muted,
			from: _event.from,
			...(currentMediaPage !== undefined && { mediaPage: currentMediaPage }),
		},
		true,
		_event.from,
	);
}, 100, { leading: true });

const setVolume = (context: any, _event: any) => {
	// console.log(`setVolume+ ${_event.from}`);
	const { currentMediaPage } = _event;

	if (_event.type === 'LOADED' && _event.muted) {
		context.audio.volume = 0;
		context.muted = true;
		context.audio.setVolume?.(0);
		context.audio.setMuted?.(true);
	} else {
		const fromAdjust = _event.from === 'update-volume-useEffect';
		if (_event.adjustedVolume) {
			context.adjustedVolume = _event.adjustedVolume;
		}
		if (!fromAdjust && typeof (_event.shownVolume) !== 'undefined') {
			context.shownVolume = parseFloat(_event.shownVolume);
		}

		// context.shownVolume = parseFloat(fromAdjust ? context.shownVolume : _event.volume);
		const currVol = context.adjustedVolume(parseFloat(
			typeof context.shownVolume === 'undefined'
				? context.volume
				: context.shownVolume,
		));
		if (context.audio) {
			context.audio.volume = currVol; // done to mute audio when volume is 0
			context.audio.setVolume?.(currVol);
			context.audio.setMuted?.(false);
		}

		context.volume = currVol;

		// context.audio.volume = currVol;
		context.muted = false;
		// console.log('Updating volume for mediaControlsContext', context.shownVolume);
		context.updateMediaControlsContext?.({ id: context.id, state: { ...context, state: { ...context } }, from: 'playVideo In Video Machine' });
	}
	if (_event.doFireSync) {
		throttledUpdatePresentedMedia(context, _event);
	}
};

const setVolumeMuted = (context: any, _event: any) => {
	// console.log(`setVolumeMuted+ ${_event.from}`);
	const { currentMediaPage } = _event;
	context.audio.volume = 0;
	context.muted = true;
	context.audio.setVolume?.(0);
	context.audio.setMuted?.(true);
	if (_event.doFireSync) {
		context.updatePresentedMedia(
			{
				muted: true,
				...(currentMediaPage !== undefined && { mediaPage: currentMediaPage }),
			},
			undefined,
			_event.from,
		);
	}
	context.updateMediaControlsContext?.({ id: context.id, state: { ...context }, from: 'setVolumeMuted In Video Machine' });
};

const setVolumeUnmuted = (context: any, _event: any) => {
	if (_event.type === 'LOADED') return;
	// console.log(`setVolumeUnmuted+ ${_event.from}`);
	// Handle undefined && 0
	const shownVolume = !Number.isNaN(Number(context.shownVolume))
		? context.shownVolume
		: 1;
	const newVol = context.adjustedVolume(parseFloat(shownVolume));
	const { currentMediaPage } = _event;
	context.audio.volume = newVol;
	context.muted = false;
	context.audio.setVolume?.(newVol);
	context.audio.setMuted?.(false);
	// if unmuted from volume event, setVolume func will send this update
	if (_event.doFireSync && _event.type !== 'VOLUME') {
		context.updatePresentedMedia(
			{
				volume: parseFloat(shownVolume),
				muted: false,
				...(currentMediaPage !== undefined && { mediaPage: currentMediaPage }),
			},
			undefined,
			_event.from,
		);
	}
	context.updateMediaControlsContext?.({ id: context.id, state: { ...context }, from: 'setVolumeUnmuted In Video Machine' });
};

export const actions = {
	setVideo,
	setElapsed,
	setCurrentTime,
	chooseTime,
	playVideo,
	pauseVideo,
	restartVideo,
	setVolume,
	setVolumeMuted,
	setVolumeUnmuted,
	setPauseFalse,
	setPauseTrue,
	setScrubbingFalse,
	setScrubbingTrue,
	setWasEndedFalse,
	setWasEndedTrue,
	onEndTrigger,
	failureFunction,
};
